import shippingCostsJson from '../config/delivery-methods.json'
import type { CartItem } from '~/types/item'
import type { DeliveryMethod } from '~/types/checkout'

export function useShipping() {
  const getShippingCosts = (itemShippingCosts: number[]): DeliveryMethod[] => {
    const parsedShippingMethodsAndCosts: DeliveryMethod[] = []

    itemShippingCosts.forEach((itemShippingCode: number) => {
      // @ts-ignore
      const parsedShipping = shippingCostsJson.find((cost: DeliveryMethod) => cost.id === itemShippingCode)
      if (parsedShipping) {
        // @ts-ignore
        parsedShippingMethodsAndCosts.push(parsedShipping)
      }
    })

    parsedShippingMethodsAndCosts.sort((a, b) =>
      a.group === 'StorePickup' && b.group !== 'StorePickup'
        ? 1
        : a.group !== 'StorePickup' && b.group === 'StorePickup'
          ? -1
          : a.price - b.price
    )
    return parsedShippingMethodsAndCosts
  }

  // Get delivery methods from the item that has 'most expensive cheapest' non-StorePickup delivery method..
  // ..if cart includes item that has only 'StorePickup'-delivery methods, return that item's delivery methods.
  const getCartHighestMinimumShippingPrices = (cartItems: CartItem[]): DeliveryMethod[] => {
    const cartItemWithOnlyPickupDelivery = cartItems.find(item => item.shipping.length && item.shipping.every(shipping => shipping.group === 'StorePickup'))

    if (cartItemWithOnlyPickupDelivery) {
      return cartItemWithOnlyPickupDelivery.shipping
    }

    const deliveryMethodsForCart = cartItems.reduce((acc: DeliveryMethod[], item) => {
      // Filter out 'StorePickup'-methods
      const nonPickupShipping = item.shipping.filter(shipping => shipping.group !== 'StorePickup')
      if (nonPickupShipping.length === 0) return acc

      const cheapestForItem = nonPickupShipping.reduce((cheapest, shipping) => shipping.price < cheapest.price ? shipping : cheapest)

      // If current item's cheapest delivery method is more expensive than the previous cheapest delivery method, use current item's delivery methods
      if (acc.length === 0 || cheapestForItem.price > acc.filter(dm => dm.group !== 'StorePickup').reduce((min, dm) => (dm.price < min.price ? dm : min)).price) {
        return [...item.shipping]
      }
      return acc
    }, [])

    return deliveryMethodsForCart.sort((a, b) => (a.price > b.price || a.price === 0) ? 1 : -1)
  }

  const getLowestMaxShippingPrice = (shippingCosts: DeliveryMethod[]): number =>
    Math.min(...shippingCosts.filter(p => p.group !== 'StorePickup').map(s => s.price)) || 0

  const getLowestCargoPrice = (shippingCosts: DeliveryMethod[]): number =>
    Math.min(...shippingCosts.filter(p => p.group !== 'StorePickup').map(s => s.priceBreakdown.shipping)) || 0

  const getOfficeFee = (shippingCosts: DeliveryMethod[]): number => {
    if (shippingCosts.length === 0) return 0
    return shippingCosts.find(s => s.priceBreakdown.officeFee > 0)?.priceBreakdown?.officeFee || 0
  }

  return {
    getShippingCosts,
    getCartHighestMinimumShippingPrices,
    getLowestMaxShippingPrice,
    getOfficeFee,
    getLowestCargoPrice
  }
}
